import {
  makeSelectedEvent,
  selectVoucherData,
} from 'app/pages/TicketSalePage/slice/selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { getPricePaidForASingleTicket } from '../utils';
import { Ticket } from 'types/TicketsState';
import {
  applyDiscountToPrice,
  buildPriceToShow,
  getFeePrice,
  getTaxPrice,
} from 'utils/tickets';
import { useTranslation } from 'react-i18next';

interface TicketPriceProps {
  ticket: Ticket;
  ticketHasDiscount: boolean;
}

const TicketPrice: React.FC<TicketPriceProps> = ({
  ticket,
  ticketHasDiscount,
}) => {
  const { t } = useTranslation();
  const { tax_rate, tax_included, currency, seatingChart, fees } =
    useSelector(makeSelectedEvent);
  const {
    voucherData: { discount, discount_percent },
  } = useSelector(selectVoucherData);

  const thePricePaidForASingleTicket = getPricePaidForASingleTicket(
    ticket.ticketprice,
    tax_rate,
    tax_included,
  );

  const firstTicketOption = ticket.ticketprice[0];
  const theFeePaidForASingleTicket = getFeePrice(
    firstTicketOption?.price,
    fees,
  );

  const initialPriceToShow = buildPriceToShow(
    thePricePaidForASingleTicket,
    currency,
    t('ticketSale.free'),
  );

  const getDisplayedPrice = () => {
    if (ticketHasDiscount && thePricePaidForASingleTicket > 0) {
      let newPrice = applyDiscountToPrice(
        firstTicketOption.price,
        discount,
        discount_percent,
      );

      if (tax_included) {
        newPrice += getTaxPrice(newPrice, tax_rate.rate);
      }

      return (
        <>
          <p className="p2-regular line-through text-neutral-800 mr-[4px] sm:mr-0 sm:absolute sm:left-[-82px] w-fit sm:w-[80px] text-right">
            {initialPriceToShow}
          </p>
          <p className="p2-semibold">
            {buildPriceToShow(newPrice, currency, t('ticketSale.free'))}
          </p>
        </>
      );
    }

    return <p className="p2-semibold">{initialPriceToShow}</p>;
  };

  const breakpoint = seatingChart ? 'md' : 'sm';

  return (
    <div
      className={`flex items-center h-fit w-fit absolute bottom-[8px] ${breakpoint}:top-0 ${breakpoint}:bottom-0 ${breakpoint}:left-[unset] ${breakpoint}:m-auto ${
        seatingChart ? 'md:right-[12px]' : 'sm:right-[160px]'
      }`}
    >
      {getDisplayedPrice()}
      {theFeePaidForASingleTicket > 0 &&
      ticket.ticketprice[0].absorb_fees === false ? (
        <p className="p4-regular ml-[4px] text-neutral-800 lowercase">
          {'+'}
          {buildPriceToShow(
            ticketHasDiscount
              ? getFeePrice(
                  applyDiscountToPrice(
                    firstTicketOption?.price,
                    discount,
                    discount_percent,
                  ),
                  fees,
                )
              : theFeePaidForASingleTicket,
            currency,
          )}
          {` (${t('ticketSale.fee')})`}
        </p>
      ) : null}
    </div>
  );
};

export default TicketPrice;
